import React, { useState } from 'react';
import classnames from 'classnames';
import {
  Box,
  Typography,
  Button,
  Iconography,
  Table,
  Image,
  formatCurrencyByCode,
} from '@lucid/lego';
import type { TableColumn } from '@lucid/lego';
import { useCartContext, useDeleteCartItem } from '@lucid/cart';
import type { CartItem } from '@lucid/cart';
import { getJellybeanImageKey } from '../../../../../../utils/tools';
import styles from './VehicleCard.module.scss';

const jellybeans = import.meta.glob<{ default: string }>('/src/assets/jellybeans/*.webp', {
  eager: true,
});

const jb = (key: string): string => jellybeans[`/src/assets/jellybeans/${key}.webp`]?.default ?? '';

const fallback = (model: string, suffix: string): string => {
  const isGravity = model.trim().toLowerCase() === 'gravity';
  const base = isGravity ? 'competitor-gravity' : 'competitor-air';
  return (
    jellybeans[`/src/assets/jellybeans/${base}-${suffix}.webp`]?.default ??
    jellybeans[`/src/assets/jellybeans/${base}.webp`]?.default ??
    ''
  );
};

type VehicleRow = {
  vin: string;
  batchNumber: string;
  wholesalePrice: string;
  model: string;
  trim: string;
};

const vehicleTableColumns = (_currencyCode?: string): TableColumn<VehicleRow>[] => [
  { key: 'vin', title: 'VIN' },
  { key: 'batchNumber', title: 'Batch #' },
  { key: 'wholesalePrice', title: 'Wholesale Price' },
  { key: 'model', title: 'Model' },
  { key: 'trim', title: 'Trim' },
];

export type VehicleCardProps = {
  item: CartItem;
  currencyCode?: string;
  theme: 'dark' | 'light';
  isExpanded: boolean;
  onToggle: () => void;
  customerId: string;
};

/**
 * Displays a single cart item as a collapsible vehicle card with a VIN table and delete action.
 * @component
 * @param {VehicleCardProps} props
 * @returns {JSX.Element}
 * @example
 * <VehicleCard item={cartItem} theme="light" isExpanded={false} onToggle={() => {}} customerId="123" />
 */
const VehicleCard: React.FC<VehicleCardProps> = ({
  item,
  currencyCode,
  theme,
  isExpanded,
  onToggle,
  customerId,
}) => {
  const { activeCart } = useCartContext();
  const cartId = activeCart?.cartId ?? '';
  const itemId = item.itemId ?? '';
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const { mutate: deleteItem, isPending: isDeleting } = useDeleteCartItem({
    cartId,
    itemId,
    customerId,
  });
  const ctx = item.itemContext as Record<string, string> | undefined;
  const modelYear = ctx?.modelYear ?? '';
  const model = ctx?.model ?? '';
  const trim = ctx?.trim ?? '';
  const exteriorColor = ctx?.exteriorColor ?? '';
  const vehicleTitle = [modelYear, model, trim].filter(Boolean).join(' ') || item.itemName;
  const totalOwed = item.lineTotalAmount ?? (item.unitPrice ?? 0) * item.quantityOrdered;

  const jellybeanBase = getJellybeanImageKey(model, trim, exteriorColor);
  const desktop1x =
    (jellybeanBase ? jb(`${jellybeanBase}-desktop`) : '') || fallback(model, 'desktop');
  const desktop2x =
    (jellybeanBase ? jb(`${jellybeanBase}-desktop_2x`) || jb(`${jellybeanBase}-desktop`) : '') ||
    fallback(model, 'desktop_2x');
  const mobile1x =
    (jellybeanBase ? jb(`${jellybeanBase}-mobile`) : '') || fallback(model, 'mobile');
  const mobile2x =
    (jellybeanBase ? jb(`${jellybeanBase}-mobile_2x`) || jb(`${jellybeanBase}-mobile`) : '') ||
    fallback(model, 'mobile_2x');
  const jellybeanImageProps = {
    uri_desktop: { '1x': desktop1x, '2x': desktop2x },
    uri_mobile: { '1x': mobile1x, '2x': mobile2x },
  };

  return (
    <Box
      className={styles.vehicleCard}
      pl={16}
      pr={16}
      theme={theme}
      data-testid={`vehicle-card-${item.itemCode}`}
    >
      <Box className={styles.vehicleCardHeader} onClick={onToggle}>
        <Typography variant="subtitle" size="small" color="black" theme={theme}>
          {vehicleTitle}
        </Typography>
        <Iconography
          category="arrows"
          name="chevron-down"
          color="black"
          className={classnames(styles.chevronIcon, { [styles.chevronExpanded]: isExpanded })}
        />
      </Box>

      {/* Always-visible body */}
      <Box className={styles.vehicleCardBody} onClick={onToggle}>
        {/* Jellybean image */}
        <Box className={styles.vehicleImage}>
          <Image
            {...jellybeanImageProps}
            alt={vehicleTitle}
            theme={theme}
            useAspectRatio={false}
            showSkeleton={false}
          />
        </Box>

        {/* QTY badge */}
        <Box className={styles.qtyBadge} data-testid={`vehicle-card-qty-${item.itemCode}`}>
          <Typography variant="body" size="extraSmall" color="white" theme={theme}>
            QTY
          </Typography>
          <Typography variant="headline" size="small" color="white" theme={theme}>
            {item.quantityOrdered}
          </Typography>
        </Box>

        {/* Data columns */}
        <Box className={styles.vehicleDataCols}>
          {exteriorColor && (
            <Box className={styles.vehicleDataCol}>
              <Typography variant="body" size="extraSmall" color="gray-5" theme={theme}>
                EXTERIOR COLOR
              </Typography>
              <Typography variant="body" size="small" color="black" theme={theme}>
                {exteriorColor}
              </Typography>
            </Box>
          )}
          <Box className={styles.vehicleDataCol}>
            <Typography variant="body" size="extraSmall" color="gray-5" theme={theme}>
              TOTAL OWED
            </Typography>
            <Typography variant="body" size="small" color="black" theme={theme}>
              {formatCurrencyByCode(totalOwed, currencyCode)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Expanded-only — VIN records table */}
      {isExpanded && (
        <Box className={styles.vehicleTable} data-testid={`vehicle-table-${item.itemCode}`}>
          <Table
            theme="light"
            compact
            dataTestId={`vehicle-table-inner-${item.itemCode}`}
            columns={vehicleTableColumns(currencyCode)}
            data={[
              {
                vin: ctx?.vin ?? item.itemCode,
                batchNumber: ctx?.batchNumber ?? '-',
                wholesalePrice: formatCurrencyByCode(item.unitPrice ?? 0, currencyCode),
                model,
                trim,
              },
            ]}
            pagination={{ enabled: false }}
            columnConfig={{ enabled: false }}
            filter={{ enabled: false }}
            selection={{
              enabled: true,
              rows: selectedRows,
              onChange: setSelectedRows,
              showExport: false,
            }}
            toolbar={{
              content: (
                <Button
                  theme={theme}
                  size="small"
                  color="error-2"
                  dataTestId={`delete-cart-item-${item.itemCode}`}
                  gaComponent="order-builder-checkout"
                  handleClick={() => deleteItem()}
                  disabled={selectedRows.length === 0 || isDeleting}
                  label={isDeleting ? 'Deleting...' : 'Delete'}
                  iconLeft={{ category: 'global', name: 'trash' }}
                />
              ),
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default VehicleCard;
